import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import { Col, Row, Tooltip } from 'antd';
import { mdiChevronRight } from '@mdi/js';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { UI54Card, UI54Table } from '@agpl/ui54kit';

import { ApiContext } from '../../api/ApiContextProvider';
import { antNotification, capitalize } from '../../mainUtils';
import { dateFormat54origins } from '../../54origins/utils54origins';
import { getAllServices } from '../../actors/selectors/index';
import { getAuthServiceInfo } from '../../auth/selectors';
import TagsSettingList from '../../components/TagsSettingList/TagsSettingList';
import BaseTag from '../../_ui/BaseTag/BaseTag';
import BaseButton from '../../_ui/BaseButton/BaseButton';
import BaseIcon from '../../_ui/BaseIcon/BaseIcon';
import TagForLogComponent from '../../logsView/components/TagForLogComponent';
import SyncLogDetailPanel from './SyncLogDetailPanel';

const ACTION_CLASS_LABELS = {
  ServiceCreateActor: 'Create Actor',
  ServiceUpdateActor: 'Update Actor',
  ServiceDeleteActor: 'Delete Actor',
  ServiceCreatePermAction: 'Create Permission',
  ServiceUpdatePermAction: 'Update Permission',
  ServiceDeletePermAction: 'Delete Permission',
  ServiceResetSessions: 'Reset Sessions',
};

function ServiceSyncLogsPage() {
  const { t } = useTranslation();
  const { requestGetAllServices, requestGetAllGroups, requestGetSyncHistoryForService } = useContext(ApiContext);
  const allServices = useSelector(getAllServices);
  const { uuid: authServiceUUID } = useSelector(getAuthServiceInfo);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);

  useEffect(() => {
    requestGetAllServices();
    requestGetAllGroups({ limit: 100, offset: 0 });
  }, []);

  useEffect(() => {
    const fetch = () => {
      setLoading(true);
      requestGetSyncHistoryForService(null)
        .then((res) => setData(Array.isArray(res) ? res : []))
        .catch(() => antNotification.error(
          capitalize(t('auth.notifications.response_error', 'data retrieval error')),
        ))
        .finally(() => setLoading(false));
    };

    fetch();
    const interval = setInterval(fetch, 5000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const allTags = useMemo(() => [
    {
      tags: [{ key: 'reset', value: 'all', label: capitalize(t('auth.headers.all', 'all')) }],
      isReset: true,
      isRadio: true,
    },
    {
      tags: allServices.filter((s) => s.uuid !== authServiceUUID).map((s) => ({
        key: 'service',
        value: s.uuid,
        label: s.uinfo?.service_name || s.uuid,
      })),
      isRow: true,
      rowText: capitalize(t('auth.headers.services', 'services')),
    },
    {
      tags: [
        { key: 'action', value: 'ServiceCreateActor', label: 'Create Actor' },
        { key: 'action', value: 'ServiceUpdateActor', label: 'Update Actor' },
        { key: 'action', value: 'ServiceDeleteActor', label: 'Delete Actor' },
      ],
      isRow: true,
      rowText: capitalize(t('auth.headers.action', 'action')),
    },
    {
      tags: [
        { key: 'status', value: 'delivered', label: capitalize(t('auth.labels.delivered', 'delivered')) },
        { key: 'status', value: 'failed', label: capitalize(t('auth.labels.failed', 'failed')) },
      ],
      isRow: true,
      rowText: capitalize(t('auth.headers.status', 'status')),
    },
  ], [allServices, t]);

  const filteredData = useMemo(() => {
    const serviceFilters = activeFilters.filter((f) => f.key === 'service').map((f) => f.value);
    const actionFilters = activeFilters.filter((f) => f.key === 'action').map((f) => f.value);
    const statusFilters = activeFilters.filter((f) => f.key === 'status').map((f) => f.value);
    return data.filter((row) => {
      if (serviceFilters.length > 0 && !serviceFilters.includes(row.target_service)) return false;
      if (actionFilters.length > 0 && !actionFilters.includes(row.action_class)) return false;
      if (statusFilters.length > 0) {
        const rowStatus = row.status ? 'delivered' : 'failed';
        if (!statusFilters.includes(rowStatus)) return false;
      }
      return true;
    });
  }, [data, activeFilters]);

  const renderActionTag = (cell) => {
    if (!cell) return '—';
    if (cell.includes('Create')) return <TagForLogComponent typeOfTag="create" />;
    if (cell.includes('Update')) return <TagForLogComponent typeOfTag="update" />;
    if (cell.includes('Delete')) return <TagForLogComponent typeOfTag="delete" />;
    return <BaseTag className="tag-gray-light">{ACTION_CLASS_LABELS[cell] || cell}</BaseTag>;
  };

  const columns = [
    {
      dataIndex: 'created',
      key: 'created',
      title: capitalize(t('auth.headers.time', 'time')),
      className: 'p-2',
      width: 140,
      render: (cell) => (cell ? dateFormat54origins(cell).dateWithTime : '—'),
    },
    {
      dataIndex: 'action_class',
      key: 'action_class',
      title: capitalize(t('auth.headers.action', 'action')),
      className: 'p-2',
      render: renderActionTag,
    },
    {
      dataIndex: 'service_name',
      key: 'service_name',
      title: capitalize(t('auth.headers.service', 'service')),
      className: 'p-2',
      render: (cell) => cell || '—',
    },
    {
      dataIndex: 'object_uuid',
      key: 'object_uuid',
      title: capitalize(t('auth.headers.object', 'object')),
      className: 'p-2',
      render: (cell, row) => {
        let name = row.object_name;
        if (!name) {
          try {
            const parseUinfo = (raw) => {
              const p = typeof raw === 'string' ? JSON.parse(raw) : (raw || {});
              return (p.actor && p.actor.uinfo) || p.uinfo || {};
            };
            const uinfo = Object.keys(parseUinfo(row.params)).length
              ? parseUinfo(row.params)
              : parseUinfo(row.previous_params);
            const fn = [uinfo.first_name, uinfo.last_name].filter(Boolean).join(' ');
            name = fn || uinfo.service_name || uinfo.group_name || null;
          } catch { /* ignore */ }
        }
        return (
          <Tooltip title={cell}>
            <span>
              {name && <span style={{ marginRight: 4 }}>{name}</span>}
              <span className="font-mono text-xs" style={{ color: '#8c8c8c' }}>{cell ? `${cell.slice(0, 8)}…` : '—'}</span>
            </span>
          </Tooltip>
        );
      },
    },
    {
      dataIndex: 'initiated_by_name',
      key: 'initiated_by_name',
      title: capitalize(t('auth.headers.by', 'by')),
      className: 'p-2',
      render: (cell) => cell?.trim() || '—',
    },
    {
      dataIndex: 'status',
      key: 'status',
      title: capitalize(t('auth.headers.status', 'status')),
      className: 'p-2',
      width: 100,
      render: (cell) => (
        <BaseTag className={`tag-${cell ? 'green' : 'red'}`}>
          {cell
            ? capitalize(t('auth.labels.delivered', 'delivered'))
            : capitalize(t('auth.labels.failed', 'failed'))}
        </BaseTag>
      ),
    },
    {
      dataIndex: 'delivery_method',
      key: 'delivery_method',
      title: capitalize(t('auth.headers.delivery_method', 'method')),
      className: 'p-2',
      width: 90,
      render: (cell) => (cell ? <BaseTag className="tag-gray-light">{cell}</BaseTag> : '—'),
    },
    {
      key: 'detail',
      title: '',
      className: 'p-2',
      align: 'right',
      render: (_, row) => (
        <BaseButton
          className="button-primary-outlined"
          size="small"
          onClick={() => setSelectedRow({ ...row, action_name: ACTION_CLASS_LABELS[row.action_class] })}
        >
          {capitalize(t('auth.buttons.detail', 'detail'))}
          <BaseIcon path={mdiChevronRight} size={1} className="ml-1" />
        </BaseButton>
      ),
    },
  ];

  return (
    <Row gutter={[24, 16]}>
      <Col span={15}>
        <UI54Card>
          <div className="mb-4">
            <TagsSettingList
              id="service_sync_logs_filters"
              allTags={allTags}
              onSelectTags={setActiveFilters}
              small
            />
          </div>
          <UI54Table
            dataSource={filteredData}
            columns={columns}
            rowKey="id"
            loading={loading}
            bordered
            tableLayout="auto"
            scroll={{ x: 'max-content' }}
            useDefaultPagination
            pagination={{ pageSizeOptions: [25, 50], defaultPageSize: 25 }}
            locale={{ emptyText: capitalize(t('auth.messages.no_sync_logs', 'no sync logs')) }}
          />
        </UI54Card>
      </Col>
      {selectedRow && (
        <Col span={9}>
          <SyncLogDetailPanel
            row={selectedRow}
            onClose={() => setSelectedRow(null)}
          />
        </Col>
      )}
    </Row>
  );
}

export default ServiceSyncLogsPage;
